<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-list dense class="py-0" :class="{ border: hasAnyLayer }">
        <template v-if="hasAnyLayer">
          <v-list-item v-for="layer in filteredLayers" :key="layer.id" class="hover-list-element">
            <v-list-item-content class="text-left">
              <v-list-item-title v-text="layer.name.replace(new RegExp('_dxf' + '$'), '')"></v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-row no-gutters class="align-center">
                <v-col cols="auto">
                  <v-tooltip color="font" bottom>
                    <template #activator="{ on }">
                      <v-icon v-on="on" color="rgba(0,0,0,.7)" size="19px" @click="zoomToExtent(layer.id)">
                        mdi-magnify
                      </v-icon>
                    </template>
                    {{ $i18n.t('navbar.zoomIn') }}
                  </v-tooltip>
                </v-col>
                <v-col cols="auto">
                  <dots-menu
                    :items="getDotsItems(layer.id)"
                    @addToProject="openConfirmationDialog(layer.id, layer.name, layer.data_source_name, 'addToProject')"
                    @delete="openConfirmationDialog(layer.id, layer.name, layer.data_source_name, 'delete')"
                    @changeName="changeName(layer.id, layer.name, layer.data_source_name)"
                  >
                  </dots-menu>
                </v-col>
              </v-row>
            </v-list-item-action>
          </v-list-item>
        </template>
        <v-list-item-content v-else>
          <v-list-item-title> {{ noResultsTranslation }}</v-list-item-title>
        </v-list-item-content>
      </v-list>
      <main-dialog
        v-if="isConfirmationDialogVisible"
        :is-visible.sync="isConfirmationDialogLoaded"
        :title="confirmationDialogTitle"
        :body="confirmationDialogBody"
        :agree-text="confirmationDialogAgree"
        :loading="isConfirmationDialogLoading"
        @agree="confirmationDialogAction"
      ></main-dialog>
      <main-input-dialog
        dialog-width="40%"
        v-if="isEditDialogLoaded"
        :is-visible.sync="isEditDialogVisible"
        :title-translation="$i18n.t('dialog.title.changeName')"
        agree-text="dialog.agreeSave"
        @agree="changeNameHandler"
        :inputs="changeNameInput"
        :loading="isEditButtonLoading"
      >
        <template #name.input="slot">
          <v-col>
            <v-row no-gutters>
              <v-col cols="12">
                <data-input v-model="dialogData.name" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </main-input-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { get, call } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';

export default {
  name: 'DxfLayersList',
  components: {
    DotsMenu,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    layers: {
      type: Array,
      required: true,
    },
    layersAll: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isConfirmationDialogLoading: false,
    isConfirmationDialogVisible: false,
    isConfirmationDialogLoaded: false,
    dialogData: {
      name: undefined,
      id: undefined,
      dataSourceName: undefined,
      type: undefined,
    },
    isEditDialogVisible: false,
    isEditDialogLoaded: false,
    isEditButtonLoading: false,
  }),
  computed: {
    featureLayerNameMaxLength: get('admin/settingsValues@feature_layer_name_max_length'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    hasAnyLayer() {
      return this.layers.length > 0;
    },
    noResultsTranslation() {
      return this.$i18n.t('sidebar.importDxf.noResults');
    },
    confirmationDialogTitle() {
      return this.dialogData.type === 'delete'
        ? this.$i18n.t('dialog.title.deleteLayer')
        : this.$i18n.t('dialog.title.addLayerToProject');
    },
    confirmationDialogBody() {
      return this.$i18n.t(
        `dialog.body.${this.dialogData.type === 'delete' ? 'confirmLayerDelete' : 'confirmLayerAddToProject'}`,
        { name: this.dialogData.name.replace(new RegExp('_dxf' + '$'), '') }
      );
    },
    confirmationDialogAction() {
      return this.dialogData.type === 'delete' ? this.deleteLayer : this.addLayerToProject;
    },
    confirmationDialogAgree() {
      return this.dialogData.type === 'delete' ? 'dialog.agreeDelete' : 'dialog.agreeAdd';
    },
    projectLayersIds() {
      return this.projectLayers.reduce((total, current) => {
        return [...total, current.id];
      }, []);
    },
    changeNameInput() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.featureLayerNameMaxLength,
          },
        },
      ];
    },
    filteredLayers() {
      return this.layers.filter(layer => ['linestring', 'multilinestring'].includes(layer.geometry_type));
    },
  },
  methods: {
    deleteDataSource: call('layers/deleteDataSource'),
    deleteLayerAction: call('layers/deleteLayerWithouUpdate'),
    editLayerAction: call('layers/editLayer'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    getDotsItems(id) {
      const pointLayerId = this.$_getDxfPointLayerByLineLayer(id, this.layers)?.id;
      const isInProject = this.projectLayersIds.includes(id) || this.projectLayersIds.includes(pointLayerId);
      return [
        {
          disabled: isInProject,
          name: 'addToProject',
          tooltip: isInProject ? this.$i18n.t('tooltips.layerAlreadyInProject') : undefined,
        },
        // Na razie nie wyrzucam całej funkcjonalności, mozliwe ze jeszcze wróci
        // {
        //   name: 'changeName',
        // },
        {
          name: 'delete',
        },
      ];
    },
    openConfirmationDialog(id, name, dataSourceName, type) {
      this.dialogData = { name, id, dataSourceName, type };
      this.isConfirmationDialogVisible = true;
      this.isConfirmationDialogLoaded = true;
    },
    changeName(id, name, dataSourceName) {
      this.dialogData = { name, id, dataSourceName, type: 'edit' };
      this.isEditDialogVisible = true;
      this.isEditDialogLoaded = true;
    },
    resetDialogData() {
      this.dialogData = {
        name: undefined,
        id: undefined,
        dataSourceName: undefined,
        type: undefined,
      };
    },
    addLayerToProject() {
      const pointLayer = this.$_getDxfPointLayerByLineLayer(this.dialogData.name, this.layers, 'name');
      if (pointLayer.geometry_type === 'point') {
        pointLayer.labels_visible = true;
      }
      const layer = this.layersAll[this.dialogData.id];
      if (layer.geometry_type === 'point') {
        layer.labels_visible = true;
      }
      this.$root.$emit('addLayersHandler', [layer, pointLayer]);
      this.isConfirmationDialogVisible = false;
      this.resetDialogData();
    },
    async deleteLayer() {
      try {
        this.isConfirmationDialogLoading = true;
        const pointLayer = this.$_getDxfPointLayerByLineLayer(this.dialogData.name, this.layers, 'name');
        await Promise.all([
          this.deleteLayerAction({ layer_id: this.dialogData.id, layer_type: 'features_layers', scope: 'dxf' }),
          this.deleteLayerAction({ layer_id: pointLayer.id, layer_type: 'features_layers', scope: 'dxf' }),
          this.deleteDataSource(this.dialogData.dataSourceName),
          this.deleteDataSource(pointLayer.data_source_name),
        ]);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successDeleteLayer'),
        });
        this.isConfirmationDialogVisible = false;
        this.isConfirmationDialogLoading = false;
        this.resetDialogData();
      } catch (error) {
        console.log('  error', error);
        this.isConfirmationDialogLoading = false;
      }
    },
    async changeNameHandler() {
      try {
        this.isEditButtonLoading = true;
        await this.editLayerAction({
          layer_id: this.dialogData.id,
          layer_type: 'features_layers',
          body: {
            name: this.dialogData.name,
          },
          scope: 'dxf',
        });
        this.isEditDialogVisible = false;
        this.isEditButtonLoading = false;
      } catch (error) {
        console.log('  error', error);
        this.isEditButtonLoading = false;
      }
    },
    async zoomToExtent(id) {
      const r = await this.getLayerFeatures({
        layer_id: id,
        params: {
          with_features: false,
          with_collection_bbox: true,
          with_count: false,
          with_geometry: false,
        },
      });
      if (r.data.data.bbox) {
        this.$root.$emit('fitView', r.data.data.bbox);
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.zoomToEmptyLayer'),
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
</style>
